import React from 'react';
import ReactGA from 'react-ga';
import classnames from 'classnames';
import {
	each as _each,
	filter as _filter,
	find as _find,
	forEach as _forEach,
	includes as _includes,
	map as _map
} from 'lodash';
import {Button, ButtonSwitch, ButtonSwitchOption, Icon,} from '@gisatcz/ptr-atoms';

import backgroundLayers from '../../../data/layers/backgroundLayers';
import wmsLayers from '../../../data/layers/wmsLayers';

import './style.scss';
import LayerControl from '../../LayerControl';
import {IntervalsLine} from '../../Legend';

const attributions = {
	osm: (
		<>
			©{' '}
			<a
				href="https://www.openstreetmap.org/copyright"
				target="_blank"
				rel="noreferrer noopener"
			>
				OpenStreetMap
			</a>{' '}
			contributors
		</>
	),
	imagery: <>Esri, USGS | Esri, Garmin, FAO, NOAA | Earthstar Geographics</>,
	topomap: <>Esri, USGS | Esri, HERE, Garmin, FAO, NOAA, USGS</>,
	greyCanvas: <>Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ</>,
	cartoDark: (
		<>
			&copy;{' '}
			<a
				href="https://www.openstreetmap.org/copyright"
				target="_blank"
				rel="noreferrer noopener"
			>
				OpenStreetMap
			</a>{' '}
			contributors,{' '}
			<a
				href="https://creativecommons.org/licenses/by-sa/2.0/"
				target="_blank"
				rel="noreferrer noopener"
			>
				CC-BY-SA
			</a>
		</>
	),
	cartoGrey: (
		<>
			&copy;{' '}
			<a
				href="https://www.openstreetmap.org/copyright"
				target="_blank"
				rel="noreferrer noopener"
			>
				OpenStreetMap
			</a>{' '}
			contributors,{' '}
			<a
				href="https://creativecommons.org/licenses/by-sa/2.0/"
				target="_blank"
				rel="noreferrer noopener"
			>
				CC-BY-SA
			</a>
		</>
	),
	occurrence: '2016 EC JRC/Google',
	change: '2016 EC JRC/Google',
	seasonality: '2016 EC JRC/Google',
	recurrence: '2016 EC JRC/Google',
	transitions: '2016 EC JRC/Google',
	evolution: '© DLR (2020)',
};

class LayersWrapper extends React.PureComponent {
	constructor(props) {
		super(props);

		this.state = {
			open: false,
		};

		this.changeState = this.changeState.bind(this);
		this.switchBackground = this.switchBackground.bind(this);
	}

	changeState() {
		let action = this.state.open ? 'Close layers' : 'Open layers';

		ReactGA.event({
			category: 'Map interaction',
			action,
			label: 'map',
		});

		this.setState({
			open: !this.state.open,
		});
	}

	switchBackground(key) {
		this.props.setBackgroundLayer(backgroundLayers[key]);
		ReactGA.event({
			category: 'Map content',
			action: `Switch background to ${key}`,
			label: 'map',
		});
	}

	onWmsLayerChange(key, e) {
		this.props.setWmsLayers(this.getWmsLayers(key));
		ReactGA.event({
			category: 'Map content',
			action: `Show/hide wms layer: ${key}`,
			label: 'map',
		});
	}

	getWmsLayers(layerKey) {
		const currentMapLayers = this.props.layers;

		let layers = [];
		const wmsLayersOrdered = wmsLayers.slice().reverse();
		_each(wmsLayersOrdered, wmsLayer => {
			const wmsLayerKey = wmsLayer.key;
			const presentInCurrentMapLayers = !!_find(
				currentMapLayers,
				mapLayer => mapLayer.key === wmsLayerKey
			);

			if (presentInCurrentMapLayers && wmsLayerKey !== layerKey) {
				layers.push(wmsLayer);
			} else if (!presentInCurrentMapLayers && wmsLayerKey === layerKey) {
				layers.push(wmsLayer);
			}
		});

		return layers;
	}

	render() {
		const props = this.props;

		const classes = classnames('eo4sdDrrInsarVietnam20-map-wrapper', {
			open: this.state.open,
		});

		const backgroundLayerKey = props.backgroundLayer?.key;
		const currentMapKey = props.mapKey;

		// TODO Button is wrapped in ptr-map-controls just for styling.
		return (
			<div className="ptr-map-wrapper">
				<div className="ptr-map-controls eo4sdDrrInsarVietnam20-layers-button">
					<div className="control">
						<Button icon="layers" onClick={this.changeState} />
					</div>
				</div>

				<div className="eo4sdDrrInsarVietnam20-map-attribution">
					<a
						href="https://leafletjs.com/"
						target="_blank"
						rel="noreferrer noopener"
					>
						Leaflet
					</a>{' '}
					| Background map: {attributions[backgroundLayerKey]}
					{this.renderWmsAttribution()}
				</div>

				<div className={classes}>
					<div className="eo4sdDrrInsarVietnam20-map-wrapper-header">
						<div>
							<Icon icon="layers" />
							<span>Layers</span>
						</div>
						<Button icon="times" invisible primary onClick={this.changeState} />
					</div>
					<div className="eo4sdDrrInsarVietnam20-map-wrapper-content">
						{currentMapKey === 'risk-assessment-big'
							? this.renderRiskAssessmentLayers()
							: this.renderInterferometryLayers()}
						{wmsLayers.length ? this.renderWmsLayers() : null}
						<div className="eo4sdDrrInsarVietnam20-layer-controls-group">
							<div className="eo4sdDrrInsarVietnam20-layer-controls-group-header">
								Background layers
							</div>
							<div>{this.renderBackgroundLayers()}</div>
						</div>
					</div>
				</div>
				{this.props.children}
			</div>
		);
	}

	renderRiskAssessmentLayers() {
		return (
			<>
				<div className="eo4sdDrrInsarVietnam20-layer-controls-group">
					<div className="eo4sdDrrInsarVietnam20-layer-controls-group-header">
						Land use blocks
					</div>
					<LayerControl layerKey="risk-assessment-zones-big" />
				</div>
				<div className="eo4sdDrrInsarVietnam20-layer-controls-group">
					<div className="eo4sdDrrInsarVietnam20-layer-controls-group-header">
						Roads
					</div>
					<LayerControl layerKey="risk-assessment-roads-big" />
				</div>
			</>
		);
	}

	renderInterferometryLayers() {
		return (
			<>
				<div className="eo4sdDrrInsarVietnam20-layer-controls-group">
					<div className="eo4sdDrrInsarVietnam20-layer-controls-group-header">
						Land use blocks
					</div>
					<LayerControl layerKey="interferometry-zones-big" />
				</div>
				<div className="eo4sdDrrInsarVietnam20-layer-controls-group">
					<div className="eo4sdDrrInsarVietnam20-layer-controls-group-header">
						Roads
					</div>
					<LayerControl layerKey="interferometry-roads-big" />
				</div>
			</>
		);
	}

	renderBackgroundLayers() {
		const activeLayerKey = this.props.backgroundLayer?.key;

		const options = _map(backgroundLayers, layer => (
			<ButtonSwitchOption
				key={layer.key}
				value={layer.key}
				active={layer.key === activeLayerKey}
			>
				{layer.name}
			</ButtonSwitchOption>
		));

		return (
			<ButtonSwitch onClick={this.switchBackground} ghost>
				{options}
			</ButtonSwitch>
		);
	}

	renderWmsLayers() {
		if (this.props.wmsLayersGroups) {
			return this.renderWmsLayersGroups();
		} else {
			return this.renderWmsLayersGroup('WMS Layers');
		}
	}

	renderWmsLayersGroups() {
		return this.props.wmsLayersGroups.map(group => {
			return this.renderWmsLayersGroup(group.name, group.attribution, group.layers);
		});
	}

	renderWmsLayersGroup(name, attribution, layerKeys) {
		return (
			<div key={name} className="eo4sdDrrInsarVietnam20-layer-controls-group">
				<div className="eo4sdDrrInsarVietnam20-layer-controls-group-header">
					<div className="eo4sdDrrInsarVietnam20-layer-controls-group-title">{name}</div>
					{attribution ? <span className="eo4sdDrrInsarVietnam20-layer-controls-group-attribution">{attribution}</span> : null}
				</div>
				<div>{this.renderWmsLayersControls(layerKeys)}</div>
			</div>
		);
	}

	renderWmsLayersControls(layerKeys) {
		const filteredWmsLayers = layerKeys
			? _filter(wmsLayers, wmsLayer => _includes(layerKeys, wmsLayer.key))
			: wmsLayers;

		return filteredWmsLayers.map(wmsLayer => {
			const key = wmsLayer.key;
			const active = !!_find(
				this.props.layers,
				currentLayer => currentLayer.key === key
			);

			return (
				<div key={key} className="eo4sdDrrInsarVietnam20-layer-control-wrapper">
					<div className="eo4sdDrrInsarVietnam20-layer-control">
						<label>
							<input
								type="checkbox"
								onChange={this.onWmsLayerChange.bind(this, key)}
								checked={active}
							/>
							{wmsLayer.name}
						</label>
					</div>
					{this.renderWmsLegend(key, active)}
				</div>
			);
		});
	}

	renderWmsAttribution() {
		const wmsLayers = this.getWmsLayers();
		let attributionList = [];

		if (wmsLayers.length) {
			_forEach(wmsLayers, wmsLayer => {
				const attribution = attributions[wmsLayer.key];
				if (attribution && !_includes(attributionList, attribution)) {
					attributionList.push(attribution);
				}
			});
		}

		if (attributionList.length) {
			return ` | ${attributionList.join(' | ')}`;
		} else {
			return null;
		}
	}

	renderWmsLegend(key, active) {
		const legendClasses = classnames(
			'eo4sdDrrInsarVietnam20-layer-control-legend',
			{
				open: active,
			}
		);

		if (key === 'occurrence') {
			return (
				<div className={legendClasses}>
					<div className="eo4sdDrrInsarVietnam20-legend-occurrence">
						<div className="eo4sdDrrInsarVietnam20-legend-bar" />
						<div className="eo4sdDrrInsarVietnam20-legend-labels">
							<span>Sometimes water (> 0 %)</span>
							<span>Always water (100 %)</span>
						</div>
					</div>
				</div>
			);
		} else if (key === 'change') {
			return (
				<div className={legendClasses}>
					<div className="eo4sdDrrInsarVietnam20-legend-change">
						<div className="eo4sdDrrInsarVietnam20-legend-bar" />
						<div className="eo4sdDrrInsarVietnam20-legend-labels">
							<span>Decrease</span>
							<span>No change</span>
							<span>Increase</span>
						</div>
					</div>
				</div>
			);
		} else if (key === 'seasonality') {
			return (
				<div className={legendClasses}>
					<div className="eo4sdDrrInsarVietnam20-legend-seasonality">
						<div className="eo4sdDrrInsarVietnam20-legend-bar" />
						<div className="eo4sdDrrInsarVietnam20-legend-labels">
							<span>Seasonal</span>
							<span>Permanent</span>
						</div>
					</div>
				</div>
			);
		} else if (key === 'recurrence') {
			return (
				<div className={legendClasses}>
					<div className="eo4sdDrrInsarVietnam20-legend-recurrence">
						<div className="eo4sdDrrInsarVietnam20-legend-bar" />
						<div className="eo4sdDrrInsarVietnam20-legend-labels">
							<span>> 0 %</span>
							<span>100 %</span>
						</div>
					</div>
				</div>
			);
		} else if (key === 'susceptibility') {
			return (
				<div className={legendClasses}>
					<div className="eo4sdDrrInsarVietnam20-legend-susceptibility">
						<div className="eo4sdDrrInsarVietnam20-legend-bar" />
						<div className="eo4sdDrrInsarVietnam20-legend-labels">
							<span>0</span>
							<span>1</span>
						</div>
					</div>
				</div>
			);
		} else if (key === 'inventory') {
			return (
				<div className={legendClasses}>
					<div className="eo4sdDrrInsarVietnam20-legend-list">
						<div className="eo4sdDrrInsarVietnam20-legend-item">
							<svg height="14" width="14">
								<polygon
									fill="#0101ff"
									points="7,1 1,14 14,14"
									className="triangle"
								/>
							</svg>
							<span>before 2015</span>
						</div>
						<div className="eo4sdDrrInsarVietnam20-legend-item">
							<svg height="14" width="14">
								<polygon
									fill="#fbff01"
									points="7,1 1,14 14,14"
									className="triangle"
								/>
							</svg>
							<span>in 2015</span>
						</div>
						<div className="eo4sdDrrInsarVietnam20-legend-item">
							<svg height="14" width="14">
								<polygon
									fill="#e8894e"
									points="7,1 1,14 14,14"
									className="triangle"
								/>
							</svg>
							<span>in or after 2015</span>
						</div>
						<div className="eo4sdDrrInsarVietnam20-legend-item">
							<svg height="14" width="14">
								<polygon
									fill="#099a1f"
									points="7,1 1,14 14,14"
									className="triangle"
								/>
							</svg>
							<span>before, in or after 2015</span>
						</div>
						<div className="eo4sdDrrInsarVietnam20-legend-item">
							<svg height="14" width="14">
								<polygon
									fill="#ff010d"
									points="7,1 1,14 14,14"
									className="triangle"
								/>
							</svg>
							<span>after 2015</span>
						</div>
					</div>
				</div>
			);
		} else if (key === 'transitions') {
			return (
				<div className={legendClasses}>
					<div className="eo4sdDrrInsarVietnam20-legend-list">
						<div className="eo4sdDrrInsarVietnam20-legend-item">
							<div style={{background: '#0000DD'}} />
							<span>Permanent</span>
						</div>
						<div className="eo4sdDrrInsarVietnam20-legend-item">
							<div style={{background: '#22B14C'}} />
							<span>New permanent</span>
						</div>
						<div className="eo4sdDrrInsarVietnam20-legend-item">
							<div style={{background: '#93073E'}} />
							<span>Lost permanent</span>
						</div>
						<div className="eo4sdDrrInsarVietnam20-legend-item">
							<div style={{background: '#99D9EA'}} />
							<span>Seasonal</span>
						</div>
						<div className="eo4sdDrrInsarVietnam20-legend-item">
							<div style={{background: '#B5E61D'}} />
							<span>New seasonal</span>
						</div>
						<div className="eo4sdDrrInsarVietnam20-legend-item">
							<div style={{background: '#B5E61D'}} />
							<span>Lost seasonal</span>
						</div>
						<div className="eo4sdDrrInsarVietnam20-legend-item">
							<div style={{background: '#FF8B37'}} />
							<span>Seasonal to permanent</span>
						</div>
						<div className="eo4sdDrrInsarVietnam20-legend-item">
							<div style={{background: '#FFDD66'}} />
							<span>Permanent to seasonal</span>
						</div>
						<div className="eo4sdDrrInsarVietnam20-legend-item">
							<div style={{background: '#7F7F7F'}} />
							<span>Ephemeral permanent</span>
						</div>
						<div className="eo4sdDrrInsarVietnam20-legend-item">
							<div style={{background: '#ACACAC'}} />
							<span>Ephemeral seasonal</span>
						</div>
					</div>
				</div>
			);
		} else if (key === 'evolution') {
			const classes = [
				{
					name: '1985',
					fill: '#D50000',
				},
				{
					name: '1986',
					fill: '#DD2200',
				},
				{
					name: '1987',
					fill: '#E54500',
				},
				{
					name: '1988',
					fill: '#EE6700',
				},
				{
					name: '1989',
					fill: '#F68A00',
				},
				{
					name: '1990',
					fill: '#FFAD00',
				},
				{
					name: '1991',
					fill: '#ECBD00',
				},
				{
					name: '1992',
					fill: '#D9CD00',
				},
				{
					name: '1993',
					fill: '#C6DE00',
				},
				{
					name: '1994',
					fill: '#B3EE00',
				},
				{
					name: '1995',
					fill: '#A1FF00',
				},
				{
					name: '1996',
					fill: '#80F200',
				},
				{
					name: '1997',
					fill: '#60E500',
				},
				{
					name: '1998',
					fill: '#40D800',
				},
				{
					name: '1999',
					fill: '#20CB00',
				},
				{
					name: '2000',
					fill: '#00BE00',
				},
				{
					name: '2001',
					fill: '#00B923',
				},
				{
					name: '2002',
					fill: '#00B546',
				},
				{
					name: '2003',
					fill: '#00B069',
				},
				{
					name: '2004',
					fill: '#00AC8C',
				},
				{
					name: '2005',
					fill: '#00A8AF',
				},
				{
					name: '2006',
					fill: '#008AB8',
				},
				{
					name: '2007',
					fill: '#006CC1',
				},
				{
					name: '2008',
					fill: '#004ECA',
				},
				{
					name: '2009',
					fill: '#0030D3',
				},
				{
					name: '2010',
					fill: '#0012DD',
				},
				{
					name: '2011',
					fill: '#190ECD',
				},
				{
					name: '2012',
					fill: '#320ABD',
				},
				{
					name: '2013',
					fill: '#4B07AD',
				},
				{
					name: '2014',
					fill: '#64039D',
				},
				{
					name: '2015',
					fill: '#7E008E',
				},
			];

			return (
				<div className={legendClasses}>
					<IntervalsLine styleProperty="fill" classes={classes} />
				</div>
			);
		} else {
			return null;
		}
	}
}

export default LayersWrapper;
